/**
 * Help Center: "missed-payments" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'payment-failed',
    title: 'What happens if a payment doesn’t go through',
    summary: 'A missed payment doesn’t cancel your trip on the spot. It shows as past due, and you can catch up from Make a Payment, all at once or from $5 at a time.',
    keywords: ['payment failed', 'declined payment', 'missed installment', 'past due', 'insufficient funds', 'expired card', 'retry'],
    related: ['missed-payments/past-due-balance', 'missed-payments/cant-pay-on-time', 'payments/update-card', 'payments/card-declined'],
    updated: '2026-10-05',
    body: `A missed payment doesn't cancel your trip on the spot. The payment shows as past due, and you can catch up from **Make a Payment** whenever you have the funds, all at once or a little at a time.

We know a failed payment can be worrying, especially with a trip ahead of you. You can sort it out yourself, right from your trip page. Here's what happens and what you can do.

## What you'll see

Open **Trips** and tap your trip. While a payment is past due:

- **Your trip shows a past-due banner.** It stays there until the missed payment is paid, or you move it to a later day.
- **The missed amount stays due.** We may try your card again for it in the meantime.
- **Make a Payment puts it first.** When you open **Make a Payment**, the past-due amount comes first. The other ways to pay open up once it's cleared.

## Why a payment might not go through

Payments can fail for everyday reasons:

- the card has expired or been replaced;
- there wasn't enough available on the card that day;
- the bank stopped the charge, or wanted you to approve it first;
- it's a prepaid card, including gift and stored-value cards, which aren't accepted on installment plans.

A prepaid card decline doesn't always say why. [Why was my card declined?](/help/payments/card-declined) goes through each reason and what to do about it.

## How to catch up

You can do this yourself, any time. Here's how:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Tap Make a Payment.** It's in the **Payment Schedule** section, where you manage your payments.
3. **Choose how much to pay.** On the **Past due** tab, enter any amount from $5. It goes toward your oldest missed payment.
4. **Pick a card and confirm.** Use your card on file, which is the card your payments are normally charged to, or add a new card.

You don't have to clear it all at once. For example, if a $90 payment was missed and you can pay $50 today, enter $50. The other $40 stays past due until you pay it.

Once nothing is past due, your plan picks up again on its normal schedule. On some older Defera Installments plans, **Make a Payment** opens a separate payment page instead, and you can clear a past-due payment there too.

## What if the card is the problem?

If your card expired, was replaced, or isn't the one you want to use anymore, swap it with **Update Card**. It's in the same **Payment Schedule** section, right next to **Make a Payment**.

- **Enter the whole card again.** It asks for the card number, expiration date, CVC and ZIP code. Saved cards can't be edited in place, so this is also how you add a new expiration date.
- **It's used from then on.** Once you save, the new card is used for all your future payments.

If a card update doesn't go through, nothing changes on your plan: the card already on file keeps being charged on schedule. Once your new card is saved, catch up on the past-due amount from **Make a Payment**.

## What if money is tight right now?

If you know when you'll have the money, tap **Missed a payment? Pick a day that works** on your trip. It moves the missed payment to the day you choose, up to {{MOVE_MAX_DAYS}} days from today, for an Extension Fee of {{MOVE_FEE_SHORT}} or {{MOVE_FEE_LONG}} spread across your remaining payments, and your trip is back on track. If your bank stopped the card or it expired, your trip may ask you to update your card first. [Moving a payment you can't make](/help/payments/move-a-payment) explains how.

There are options for your upcoming payments too, on plans that show them:

- **Skip Payment** makes your next payment $0 for a {{SKIP_FEE}} fee. It isn't available while a payment is past due, but it's there again once you've caught up.
- **Change Schedule** switches you from bi-weekly to smaller weekly payments, with no fee.

[I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) walks through each one.

## What if a payment stays past due?

If a payment stays past due for a long time, your plan can pause. That's recoverable: your trip shows a **Restart Plan** option that gets your payments going again.

If payments fall far enough behind, a trip can default, which means its payment plan ends. [What happens if my trip defaults?](/help/missed-payments/what-is-default) explains what that means and the possible ways forward.

## Need a hand?

[Chat with the Defera Concierge](/support), our AI concierge, any time. It can look at your trip with you, go through your options and point you to the right button.

If you need more time to pay than these options give you, tell the concierge what's going on and when you expect to be able to pay. It can send a request to our team. Your request shows as a card on your trip page, and the answer posts there and comes by email, usually within 24 hours.`,
  },
  {
    slug: 'cant-pay-on-time',
    title: 'I can’t make my next payment. What are my options?',
    summary: 'You have options: move the payment up to {{MOVE_MAX_DAYS}} days later, skip it for {{SKIP_FEE}}, or switch to smaller weekly payments for free. Here’s how each works.',
    keywords: ['hardship', 'can’t afford', 'money is tight', 'skip a payment', 'lower payments', 'more time to pay', 'move due date', 'payday'],
    related: ['payments/move-a-payment', 'payments/skip-a-payment', 'payments/change-schedule', 'missed-payments/past-due-balance'],
    updated: '2026-10-25',
    body: `If your next payment is going to be hard to make, you have options, and you can use them yourself from your trip. You can move the payment to the day you'll be able to pay, up to {{MOVE_MAX_DAYS}} days later, with nothing charged today. You can also skip the next payment for a {{SKIP_FEE}} fee, or switch to smaller weekly payments at no cost.

We know it's stressful when a payment is coming up and the money isn't there yet. It helps to act before the payment is due: once a payment is past due, some of these options aren't available until you've caught up.

## Your options at a glance

Here's how the options compare. Which ones you see depends on your plan.

| Option | What it costs today | What changes |
|---|---|---|
| **Can't make your payment?** | Nothing | Your payment moves up to {{MOVE_MAX_DAYS}} days later. The Extension Fee ({{MOVE_FEE_SHORT}} or {{MOVE_FEE_LONG}}) is spread across your remaining payments. |
| **Skip Payment** | A {{SKIP_FEE}} fee | Your next payment becomes $0. The payments left go up slightly to cover it. |
| **Change Schedule** to weekly | Nothing | Your remaining balance is spread across smaller, more frequent payments. |
| **Pick my own dates** | A {{DATE_CHANGE_FEE}} fee | You choose the date of your next payment. |

In short: if you know when you'll have the money, move the payment to that day. If each payment is too big, switching to weekly costs nothing. If you need the next payment to be $0, that's **Skip Payment**.

Moving a payment and **Pick my own dates** both change a date. Moving a payment is for a payment you can't make: nothing is charged today, the fee is spread over your remaining payments, and it works on a missed payment too. **Pick my own dates** is for lining your dates up with payday ahead of time: its {{DATE_CHANGE_FEE}} is charged when you confirm, and anything past due has to be paid first.

## Move the payment to the day you can pay

Under the Payment Schedule buttons on your trip, tap **Can't make your payment?** and pick the day you'll be able to pay, up to {{MOVE_MAX_DAYS}} days later.

- **The cost:** an Extension Fee of {{MOVE_FEE_SHORT}} for up to {{MOVE_FEE_SHORT_DAYS}} days later, or {{MOVE_FEE_LONG}} for up to {{MOVE_MAX_DAYS}}. It's spread across your remaining payments, so nothing is charged today.
- **Your other payments move with it,** keeping the same spacing, and still finish at least {{DATE_CHANGE_CUTOFF_DAYS}} days before departure.
- **The limits:** each payment can move once, and you can move a payment {{MOVES_PER_TRIP}} times per trip. The fee isn't refunded if you cancel later.

It works on a payment that already didn't go through, too. [Moving a payment you can't make](/help/payments/move-a-payment) has the details.

## Skip your next payment

**Skip Payment** makes your next payment $0. It's available on Defera Installments plans that show this option. Here's how it works:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Tap Skip Payment.** It has its own button in the **Payment Schedule** section, separate from **Make a Payment**.
3. **Check your new amounts.** The screen shows exactly what each remaining payment will be before you confirm anything.
4. **Confirm.** Your next payment becomes $0, and the {{SKIP_FEE}} fee is charged to your card on file today.

The payments that are left go up slightly to cover the skipped amount. For example, say you have $360 left to pay, in nine payments of $40. If you skip the next one, the $360 is spread over the eight payments that are left, so each becomes $45.

Your payment dates don't move, so your next charge lands on your following scheduled date. For example, if you pay every two weeks and skip the payment due on the 1st, your next charge is on the 15th. On a weekly plan, it's one week later.

A couple of things to know:

- The {{SKIP_FEE}} fee can't be waived.
- **Skip Payment** isn't available while a payment is past due. Once you've caught up, it's available again.

## Switch to smaller weekly payments

If the size of each payment is what's hard, switching from bi-weekly to weekly spreads your remaining balance into smaller, more frequent payments. There's no fee, and nothing is charged today.

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Tap Change Schedule.** It's in the **Payment Schedule** section.
3. **Compare, then confirm.** You'll see your current and new dates and amounts side by side before anything changes.

On some older Defera Installments plans, **Request Extension** sits where **Change Schedule** usually is. It moves your final payment date later, for a {{EXTENSION_FEE}} fee.

## Move your payment to a better date

If the problem is timing, for example your payment lands a few days before payday, **Change Schedule** also offers **Pick my own dates** on plans that show it. You choose the date of your next payment.

- **The cost:** {{DATE_CHANGE_FEE}}, charged to your card on file when you confirm.
- **One fee covers the day:** change your date in the morning and again in the afternoon, and you pay once. A change on another day is a new fee.
- **The timing rule:** each new date has to be at least {{DATE_CHANGE_CUTOFF_DAYS}} days before departure.
- **Past due comes first:** anything past due has to be paid before you can pick new dates.

For example, if your payment is due on the 3rd and you get paid on the 5th, you could move it to the 5th for {{DATE_CHANGE_FEE}}. [Picking your own payment dates](/help/payments/pick-your-dates) has the details.

## What if a payment fails anyway?

It happens, and your trip isn't cancelled on the spot. Here's what to expect:

- **Your trip shows a past-due banner.** The missed amount stays past due until it's paid or moved to a later day, and we may try your card again.
- **You can catch up any time.** In **Make a Payment**, the **Past due** tab takes any amount from $5 toward your oldest missed payment.
- **Your plan picks up again.** Once nothing is past due, your payments return to their normal schedule.

A plan that stays past due for a long time can pause. If that happens, your trip shows a **Restart Plan** option to get it going again. [Catching up on a past-due balance](/help/missed-payments/past-due-balance) walks through it.

## Need more time?

If none of these fit, for example you need more time to pay, [chat with the Defera Concierge](/support), our AI concierge. Tell it what's going on and when you expect to be able to pay. It can send a request to our team, who'll review it.

Your request shows as a card on your trip page. The answer posts there and comes to you by email, usually within 24 hours.`,
  },
  {
    slug: 'still-taking-this-trip-payments',
    title: 'The "Are you still taking this trip?" email',
    summary: 'We send it when a payment keeps failing. Still going? Catch up from Make a Payment. Plans changed? Cancel for 100% of what you’ve paid back as Defera Credit.',
    keywords: ['payment keeps failing', 'are you still taking this trip', 'payments not going through', 'is this email real', 'plans changed', 'missed payment email', 'recoup what I paid'],
    related: ['missed-payments/past-due-balance', 'missed-payments/payment-failed', 'changes-cancellations/what-is-defera-credit', 'changes-cancellations/cancel-your-trip'],
    updated: '2026-10-23',
    body: `This email is a real, automatic check-in from Defera. We send it when a payment on your trip has been tried several times and still hasn't gone through. It asks one thing: are you still planning to take this trip?

Either way, you can act on your answer from your trip in the app, and the choice is yours.

## Why did I get it?

One of your trip's payments didn't go through, and the automatic retries haven't been able to collect it either. Rather than let that drift, we'd like to hear from you. We send it at most once for each missed payment.

It isn't a cancellation notice. Nothing has been cancelled, and your trip is still yours.

## I'm still going. What should I do?

Catch up on the missed payment from your trip:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Find the Payment Schedule section.** It shows what's past due.
3. **Tap Make a Payment.** Pay what's past due with the card on file or a different card.
4. **Update your card if it was the problem.** Tap **Update Card** in the same section, so your next payments go through on their dates.

[Catching up on a past-due balance](/help/missed-payments/past-due-balance) walks through it in more detail.

## My plans have changed. What should I do?

This is the perfect time to recoup what you've paid. If your trip's cancel screen offers **Cancel & receive Defera Credit**, you get 100% of what you've paid back as Defera Credit. Your plan stops, and no further payments are taken.

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Open your cancel screen.** Tap **Flight Options**, then **Cancel**. Opening it doesn't cancel anything by itself.
3. **Check the amount.** The screen shows exactly what you'll get back before anything is final.
4. **Cancel for credit.** Tap **Cancel & receive Defera Credit**. The credit is on your account instantly.

Defera Credit never expires, and you can use it toward any future trip, including trips you book for someone else. [What is Defera Credit?](/help/changes-cancellations/what-is-defera-credit) explains how it works.

> [!IMPORTANT] The Defera Credit option isn't available indefinitely, so it's worth deciding soon, whichever way you're leaning.

## Not sure yet?

Reply to the email, or [chat with the Defera Concierge](/support), our AI concierge, any time. It can look at your trip's payments and options with you.`,
  },
  {
    slug: 'past-due-balance',
    title: 'Catching up on a past-due balance',
    summary: 'You can catch up any time from Make a Payment, all at once or from $5 at a time. Once nothing is past due, your plan goes back to its normal schedule.',
    keywords: ['overdue', 'behind on payments', 'missed payment', 'pay what I owe', 'partial payment', 'restart plan', 'paused plan'],
    related: ['missed-payments/payment-failed', 'missed-payments/cant-pay-on-time', 'payments/make-a-payment', 'payments/update-card'],
    updated: '2026-10-05',
    body: `You can catch up on a past-due balance any time from **Make a Payment** on your trip. You don't have to pay it all at once: you can pay any amount from $5, with your card on file or a new card.

A past-due payment doesn't cancel your trip on the spot, and every amount you pay goes toward what you've missed. Once nothing is past due, your plan picks up again on its normal schedule.

## What “past due” means

A payment is past due when its date has passed and it hasn't been paid, usually because the charge to your card didn't go through. While it's past due, your trip shows a past-due banner, and we may try your card again.

## How to pay what's past due

Here's what to do:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Tap Make a Payment.** It's in the **Payment Schedule** section. While something is past due, it comes first, and the other ways to pay open up once it's cleared.
3. **Enter an amount.** On the **Past due** tab, type how much you'd like to pay, from $5.
4. **Pick a card and confirm.** Use your card on file, which is the card your payments are normally charged to, or add a new card.

On some older Defera Installments plans, **Make a Payment** opens a separate payment page instead. You can clear a past-due payment there too.

## Can I pay part of it?

Yes. Whatever you pay on the **Past due** tab goes toward your oldest missed payment. Anything you don't cover stays past due until you pay it, and we may try your card for it again.

For example, if a $100 payment was missed and you can pay $60 today, enter $60. The other $40 stays past due, and you can pay it from **Make a Payment** whenever you're ready.

## What happens once you're caught up

- **Your plan resumes.** Your payments go back to their normal schedule.
- **Your options come back.** **Skip Payment** and **Pick my own dates** are available again, on plans that offer them.

Need a few more days instead? **Missed a payment? Pick a day that works** on your trip moves the missed payment up to {{MOVE_MAX_DAYS}} days from today, and your trip is back on track. The Extension Fee is spread across your remaining payments. [Moving a payment you can't make](/help/payments/move-a-payment) explains how.

If your upcoming payments look hard too, [I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) covers what you can do.

## What if my plan has paused?

If a payment stays past due for a long time, your plan can pause. That's recoverable. Your trip shows a **Restart Plan** option that gets your payments going again.

## What if my card was the reason?

If the payment failed because your card expired or was replaced, add the new one with **Update Card**, in the same **Payment Schedule** section. It asks for the full card details, and once you save, the new card is used for all your future payments. Then pay what's past due from **Make a Payment**. [Updating your card](/help/payments/update-card) has the steps.

## What if I can't pay it all yet?

Pay what you can. Paying part of it still helps, because every payment goes toward what you've missed.

If you need more time, [chat with the Defera Concierge](/support), our AI concierge, any time. Tell it what's going on and when you expect to be able to pay, and it can send a request to our team. The answer posts on your trip page and comes by email, usually within 24 hours.`,
  },
  {
    slug: 'what-is-default',
    title: 'What happens if my trip defaults?',
    summary: 'If payments fall far enough behind, a trip can default: its plan ends and automatic payments stop. There may still be a way forward, and you can ask from your trip.',
    keywords: ['defaulted trip', 'payment default', 'fell behind on payments', 'unpaid balance', 'plan ended', 'automatic payments stopped', 'contact support'],
    related: ['missed-payments/defaulted-trip-options', 'missed-payments/past-due-balance', 'changes-cancellations/what-is-defera-credit', 'contact/how-requests-work'],
    updated: '2026-10-05',
    body: `If payments fall far enough behind, a trip can default. When that happens, the trip's payment plan ends, and automatic payments stop.

We know this is a stressful moment. There may still be a way forward, and you can ask about it right from your trip page. Here's what a default means, what you'll see and what you can do.

## What “default” means

In plain words, a default means the payment plan for your trip has ended because payments fell far enough behind. Two things change:

- **The payment plan ends.** Your trip's schedule of payments stops.
- **Automatic payments stop.** We no longer charge your card automatically for this trip.

## What you'll see on your trip

Open **Trips** and tap the trip. You'll see:

- **The trip marked as defaulted.** Your trip page shows it plainly.
- **Flight Options grayed out.** The **Flight Options** menu, where you'd normally change flights, add seats and bags or cancel, can't be opened.
- **A Contact Support button.** Tapping **Contact Support** opens a chat with the Defera Concierge, our AI concierge, so you can ask about a way forward.

## What happens to what I still owe?

What happens to any unpaid balance after a default is set out in our [Terms of Service](/terms). If the legal wording is hard to follow, ask the Defera Concierge, and it can explain it in plain English.

## Is there a way forward?

There may be. There are two possible ways forward. Both are offered on select bookings, and eligibility may vary:

- **Turning what you've paid into Defera Credit.** Defera Credit is credit on your Defera account that you can put toward a future trip, including one for someone else. It never expires.
- **Reinstating the trip.** Our team brings the trip back on a fresh payment schedule for your remaining balance. There's a {{REINSTATEMENT_FEE}} reinstatement fee, charged to your card on file (the card your payments were charged to) when our team processes it.

We can't say here which option, if either, applies to your trip, but the concierge can tell you. [Options for a defaulted trip](/help/missed-payments/defaulted-trip-options) explains both in more detail.

## How to ask

Here's what happens:

1. **Tap Contact Support.** It's on your trip page. You can also [chat with the Defera Concierge](/support) and say which trip it's about.
2. **Ask about your options.** The concierge tells you what's available for your booking. If both are available, the choice is yours.
3. **Choose, and it sends your request.** If you choose to reinstate, the concierge tells you about the {{REINSTATEMENT_FEE}} fee and checks that you're okay with it first. Then it sends your request to our team.
4. **Watch for the answer.** Your request shows as a card on your trip page. The answer posts there and comes to you by email, usually within 24 hours.

## What if my trip hasn't defaulted?

If a payment is past due but your trip hasn't defaulted, you can catch up from **Make a Payment** on your trip, any amount from $5 at a time. Once nothing is past due, your plan picks up its normal schedule again. [Catching up on a past-due balance](/help/missed-payments/past-due-balance) shows how.`,
  },
  {
    slug: 'defaulted-trip-options',
    title: 'Options for a defaulted trip',
    summary: 'On select bookings, what you’ve paid can become Defera Credit, or the trip can be reinstated for a {{REINSTATEMENT_FEE}} fee. Eligibility may vary. Here’s how to ask.',
    keywords: ['reinstate', 'reinstatement fee', 'restore my trip', 'get my trip back', 'convert to credit', 'defaulted booking', 'way forward'],
    related: ['missed-payments/what-is-default', 'changes-cancellations/what-is-defera-credit', 'contact/how-requests-work', 'contact/check-request-status'],
    updated: '2026-10-05',
    body: `A defaulted trip may have two ways forward. What you've paid can be turned into Defera Credit, or the trip can be reinstated on a fresh payment schedule for a {{REINSTATEMENT_FEE}} fee. Both are offered on select bookings, and eligibility may vary.

To find out what's available for your trip, tap **Contact Support** on your trip page. The Defera Concierge, our AI concierge, tells you which options apply to your booking. Once you've chosen, it sends your request to our team.

## Turning what you've paid into Defera Credit

Defera Credit is credit on your Defera account that you can use toward a future trip with us. With this option, what you've paid on the defaulted trip becomes Defera Credit.

- **It never expires.** It's there whenever you're ready to travel.
- **It's for anyone you book for.** Use it on your own trip, or on a trip you book for someone else, like a partner, a parent or a friend.
- **It applies at checkout.** When you book, your credit applies automatically, and you can choose how much of it to use.
- **It isn't cash.** It's for travel with Defera, so it can't be cashed out or refunded to a card.

[What is Defera Credit?](/help/changes-cancellations/what-is-defera-credit) explains how it works.

## Reinstating your trip

Reinstating brings your trip back. Our team sets up a fresh payment schedule for your remaining balance, and your payment plan picks up again.

- **The fee is {{REINSTATEMENT_FEE}}.** It's charged to your card on file (the card your payments are charged to) when our team processes your request.
- **You agree to the fee first.** The concierge tells you about it, and checks that you're okay with it, before it sends your request.
- **Our team confirms the details.** Your new payment dates and amounts are confirmed when our team processes your request.

## How to ask

Here's what happens, step by step:

1. **Open the chat.** On your trip page, tap **Contact Support**, or open the [Defera Concierge](/support) chat.
2. **Ask about your options.** Tell the concierge you'd like to know what's available for your trip.
3. **Hear what applies.** The concierge tells you which options are available for your booking. If both are available, the choice is yours.
4. **Make your choice.** Once you've chosen, the concierge sends your request to our team.
5. **Get your answer.** Your request shows as a card on your trip page. The answer posts there and comes to you by email, usually within 24 hours.

If neither option is available for your booking, the concierge will tell you.

## What to expect

- **Nothing is charged when you ask.** If your trip is reinstated, the {{REINSTATEMENT_FEE}} fee is charged when our team processes it.
- **The answer is specific to your booking.** Eligibility varies by booking, so we can't say here which option will apply to yours.
- **You can follow along.** Your request's card reads "Submitted · in progress" while our team works on it, then shows the answer.

## What about the unpaid balance?

What happens to any unpaid balance after a default is set out in our [Terms of Service](/terms). If the wording is hard to follow, the Defera Concierge can explain it in plain English.`,
  },
  {
    slug: 'payment-reminders',
    title: 'Keeping track of your upcoming payments',
    summary: 'Your payments are charged automatically on their dates. See what’s coming any time in your trip’s Payment Schedule, or download your Trip Statement.',
    keywords: ['payment reminder', 'reminder email', 'next payment', 'due date', 'when am I charged', 'payment date', 'notification'],
    related: ['payments/view-payment-schedule', 'payments/trip-statement', 'emails/payment-emails', 'missed-payments/payment-failed'],
    updated: '2026-10-05',
    body: `Your payments are charged automatically to your card on file, on the dates in your payment schedule. There's nothing you need to do on the day. To see what's coming, open your trip's **Payment Schedule** in **Trips** any time.

Your card on file is the card your payments are charged to, so keeping it up to date helps each payment go through. Here's where to see your upcoming payments, and how to stay ahead of them.

## Where to see what's coming

Here's how to check your upcoming payments:

1. **Open Trips.** It's in the main menu.
2. **Tap your trip.** Each trip has its own payment schedule, so if you have more than one trip, check each one.
3. **Find the Payment Schedule section.** It's on the left on a computer and at the top on your phone, and it shows your upcoming payments.

## A record you can keep

Your Trip Statement is a PDF of your trip. It lists your payment plan and its history: what you've paid, what's coming up and anything past due. To download it, open the trip and tap the download icon in the trip's header row, next to the message and call support buttons.

Your booking confirmation email also has a payment summary from the day you booked. [Downloading your Trip Statement](/help/payments/trip-statement) explains what else the statement shows.

## Want a heads-up before each payment?

Because payments are automatic, you don't need to remember to pay. If you'd like a nudge anyway, add the dates from your **Payment Schedule** to your phone's calendar or reminders. That way you'll know when each payment is coming, and you can make sure the money is there.

## Why a payment might look different

Your upcoming payments can change when you use one of the options on your trip:

- **Pay ahead**, in **Make a Payment**, pays upcoming payments now, so your next automatic charge moves later.
- **Skip Payment** makes your next payment $0, and the payments left go up slightly.
- **Change Schedule** switches between weekly and bi-weekly, and recalculates your future dates and amounts.
- **Apply Credits** puts Defera Credit you received after booking toward your upcoming payments, earliest first.

[Viewing your payment schedule](/help/payments/view-payment-schedule) explains how to read your schedule.

## Avoiding surprises

- **Keep your card up to date.** If your card expires or is replaced, tap **Update Card** in the **Payment Schedule** section. The new card is used for all your future payments.
- **Act before a tough week.** If an upcoming date isn't going to work, you can make changes before it's due, for example by skipping a payment or switching to smaller weekly payments. [I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) covers each option.

## If a payment doesn't go through

Your trip shows a past-due banner, and you can catch up from **Make a Payment**, from $5 at a time. A missed payment doesn't cancel your trip on the spot. [What happens if a payment doesn't go through](/help/missed-payments/payment-failed) explains the rest.

## Questions about your schedule?

[Chat with the Defera Concierge](/support), our AI concierge, any time. It can look at your trip's payments with you.`,
  },
  {
    slug: 'good-standing',
    title: 'What “good standing” means',
    summary: 'Good standing means keeping up with your payment plan. While you do, your agreed-upon trip price stays protected, even if airfares go up.',
    keywords: ['price protection', 'price lock', 'on track', 'current on payments', 'fare increase', 'price secured', 'stay protected'],
    related: ['getting-started/price-protection', 'confirmation-and-travel/price-protection-and-confirmation', 'missed-payments/past-due-balance', 'missed-payments/cant-pay-on-time'],
    updated: '2026-10-05',
    body: `Being in good standing means keeping up with your payment plan. While you're in good standing, your agreed-upon trip price stays protected: if airfares go up after you book, your price doesn't.

That protection starts once your booking is approved, on both Defera Installments and Defera Zero. You don't need to do anything to switch it on.

## What price protection means for you

When you book, you agree on a price for your trip. Once your booking is approved, that price is secured with Defera. If airfares go up afterward, your price stays the same, as long as you remain in good standing.

For example, say you book a trip for $600. A month later, the same flights cost $750. Your trip price is still $600, because it was secured when your booking was approved, and you've kept up with your plan.

You'll see this promise at checkout too: "Price protected from today" appears above your total.

## When protection starts

- **Defera Installments:** once your deposit is paid and your booking is approved.
- **Defera Zero:** once your booking is approved, with $0 down.

## How is this different from my confirmation number?

They're two separate things:

- **Price protection** secures your trip price. It starts once your booking is approved and lasts while you're in good standing.
- **Your confirmation number** is the code the airline uses to find your reservation, and you'll use it to check in. It's released once your trip is paid in full.

So it's completely normal to have a protected price and no confirmation number yet. [Price protection vs. your confirmation number](/help/confirmation-and-travel/price-protection-and-confirmation) explains how the two fit together.

## How to stay in good standing

A few habits make it easier to keep up:

- **Know what's coming.** Your trip's **Payment Schedule** shows your upcoming payments. Open **Trips** and tap your trip.
- **Keep your card up to date.** Your payments are charged automatically to your card on file. If it expires or is replaced, tap **Update Card** in the **Payment Schedule** section.
- **Adjust before a payment is due.** If a date isn't going to work, **Skip Payment** and **Change Schedule** let you change things ahead of time, on plans that show them.
- **Get ahead when you can.** If you have extra money now, **Pay ahead** in **Make a Payment** pays upcoming payments today and moves your next automatic charge later.

[I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) explains each option if money is tight.

## What if I've fallen behind?

It happens, and you can catch up any time from **Make a Payment**, from $5 at a time. Once nothing is past due, your plan picks up its normal schedule again. [Catching up on a past-due balance](/help/missed-payments/past-due-balance) walks you through it.

If you have a question about where your own plan stands, the Defera Concierge, our AI concierge, can look at it with you any time.

## Is this about my credit score?

No. Good standing is about your payment plan with Defera. We don't check your credit when you book, and we don't report your plan or your on-time payments to the credit bureaus. What happens if a plan goes unpaid is set out in our [Terms of Service](/terms).`,
  },
];
